import Fuse from 'fuse.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  buildSuggestions,
  resolveClearBossCharacter,
  titleCase,
  type PaletteBossPlan,
  type PaletteCharacter,
  type Suggestion,
  type SuggestionGroup,
} from '../lib/commandPalette';
import { bossMatrixQuery, useUpsertBossClear, useUpsertBossPlan } from '../lib/bosses';
import { periodKeyForTier } from '../lib/bossPeriod';
import { pushToast } from '../lib/events';
import { dailyKey, monthlyKey, weeklyKey } from '../lib/period';
import { classDisplayName, useClasses } from '../lib/reference';
import { avatarProxyPath, useAccounts } from '../lib/roster';
import { BossIcon } from './bosses/BossIcon';
import { selectClassNames } from './selectStyles';

const GROUP_ORDER: SuggestionGroup[] = ['Pages', 'Commands', 'Characters', 'Bosses'];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** The palette's avatar circle for a `Characters`-group row — mirrors `CharacterTabStrip`'s
 *  `TabAvatar` (REV12 proxy path + initial-letter fallback on a load failure). */
function SuggestionAvatar({ src, label }: { src: string | null; label: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-elevated-hover text-xs font-semibold text-text-faint">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{label.trim().slice(0, 1).toUpperCase() || '?'}</span>
      )}
    </span>
  );
}

/**
 * The `:` command palette (REV14 — docs/specs/REV14-command-palette.md; extended REV16 —
 * docs/specs/REV16-clearboss-and-toastify.md — with the `clearboss` command). Fully hidden by
 * design: no visual affordance anywhere, no mobile trigger — a global keydown is the only entry
 * point. Mounted once in `AppShell` so it's available on every authed page with router context.
 *
 * Deliberately does NOT use `components/Modal.tsx`: Modal's own window-Escape listener is
 * unconditional (`onClose()` with no guard), so a second Modal-based overlay stacked on top of an
 * already-open Modal would close *both* on one Escape press. Instead this owns a capture-phase
 * window Escape listener with `stopPropagation`, which — because capture fires top-down before any
 * bubble-phase listener on the same `window` target — always intercepts the keydown before Modal's
 * bubble listener ever sees it.
 */
export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const pressStartedOnBackdrop = useRef(false);
  const navigate = useNavigate();
  const accountsQuery = useAccounts();
  // REV18: resolves `classSlug` -> friendly display name for the `characters` memo below — the
  // pure `lib/commandPalette.ts` never touches this query itself (see `PaletteCharacter.classDisplayName`).
  const classesQuery = useClasses();
  const upsertPlan = useUpsertBossPlan();
  const upsertClear = useUpsertBossClear();

  const characters = useMemo<PaletteCharacter[]>(
    () =>
      (accountsQuery.data ?? []).flatMap((account) =>
        account.characters.map((character) => ({
          characterId: character.id,
          accountId: character.accountId,
          accountLabel: account.label,
          name: character.name,
          classSlug: character.classSlug,
          classDisplayName: classDisplayName(character.classSlug, classesQuery.data),
          level: character.level,
          avatarUrl: character.avatarUrl,
        })),
      ),
    [accountsQuery.data, classesQuery.data],
  );

  // screenshotImport's Fuse idiom (`keys: ['name'], threshold: 0.35, ignoreLocation: true`) — built
  // once per roster change, not per keystroke, and never inside the pure lib (see commandPalette.ts).
  const characterFuse = useMemo(
    () => new Fuse(characters, { keys: ['name'], threshold: 0.35, ignoreLocation: true }),
    [characters],
  );

  // REV16: `clearboss`'s current-period reference point — always "now", never a viewed-in-the-past
  // week the way Bosses.tsx's own weekKey can be, since the palette only ever acts on the live
  // matrix. Recomputed every render, same as Bosses.tsx's own `now`.
  const now = new Date();
  const todayIso = dailyKey(now);
  const currentWeekKeyIso = weeklyKey(now);
  const currentMonthKeyIso = monthlyKey(now);

  // The pure lib decides — from the raw input alone — whether a `clearboss` character token has
  // been committed (exact name + trailing space). Only in that state does the lazy boss-matrix
  // fetch turn on, and only for that character's account.
  const resolvedCharacter = resolveClearBossCharacter(input, characters);
  const matrixQuery = useQuery({
    ...bossMatrixQuery(resolvedCharacter?.accountId ?? -1, currentWeekKeyIso),
    enabled: resolvedCharacter !== null,
  });

  const plannedBosses = useMemo<PaletteBossPlan[]>(() => {
    const matrixData = matrixQuery.data;
    if (resolvedCharacter === null || !matrixData) return [];
    const character = matrixData.characters.find((c) => c.characterId === resolvedCharacter.characterId);
    if (!character) return [];
    return character.plans.map((plan) => {
      const periodKey = periodKeyForTier(plan.tier, {
        weekKey: currentWeekKeyIso,
        todayIso,
        currentMonthKeyIso,
        monthlyKeys: matrixData.monthlyKeys,
      });
      return {
        bossSlug: plan.bossSlug,
        bossName: plan.bossName,
        tier: plan.tier,
        sharedClearGroup: plan.sharedClearGroup,
        difficulty: plan.difficulty,
        partyMax: plan.partyMax,
        alreadyClearedThisPeriod: character.clears.some(
          (c) => c.bossSlug === plan.bossSlug && c.periodKey === periodKey,
        ),
      };
    });
  }, [resolvedCharacter, matrixQuery.data, currentWeekKeyIso, todayIso, currentMonthKeyIso]);

  const bossFuse = useMemo(
    () => new Fuse(plannedBosses, { keys: ['bossName'], threshold: 0.35, ignoreLocation: true }),
    [plannedBosses],
  );

  const suggestions = useMemo(
    () =>
      buildSuggestions(input, {
        characters,
        searchCharacters: (query) => characterFuse.search(query).map((result) => result.item),
        plannedBosses,
        searchPlannedBosses: (query) => bossFuse.search(query).map((result) => result.item),
      }),
    [input, characters, characterFuse, plannedBosses, bossFuse],
  );

  const close = () => {
    setIsOpen(false);
    setInput('');
    setHighlightedIndex(0);
  };

  /**
   * `clearboss`'s execution: an optional plan upsert (only when `[players]` was typed) followed by
   * the clear upsert itself, both looked up fresh from the already-fetched matrix cache so the
   * fields the pure lib's lean `clearBoss` action doesn't carry (crystalValue/crystalType, and the
   * plan's other fields for the party-size upsert) are always current. A mutation failure is
   * already toasted + rolled back by its own `onError` (lib/bosses.ts) — this just has to avoid an
   * unhandled rejection, same swallow-and-move-on shape as Bosses.tsx's `handleCompleteAllTier`.
   */
  const executeClearBoss = async (action: Extract<Suggestion['action'], { kind: 'clearBoss' }>) => {
    const character = matrixQuery.data?.characters.find((c) => c.characterId === action.characterId);
    const plan = character?.plans.find((p) => p.bossSlug === action.bossSlug);
    if (!plan) {
      return;
    }
    const periodKey = periodKeyForTier(action.tier, {
      weekKey: currentWeekKeyIso,
      todayIso,
      currentMonthKeyIso,
      monthlyKeys: matrixQuery.data?.monthlyKeys ?? [currentMonthKeyIso],
    });
    try {
      if (action.partySize !== undefined) {
        await upsertPlan.mutateAsync({
          accountId: action.accountId,
          weekKey: currentWeekKeyIso,
          characterId: action.characterId,
          bossSlug: action.bossSlug,
          bossName: plan.bossName,
          tier: plan.tier,
          sharedClearGroup: plan.sharedClearGroup,
          difficulty: plan.difficulty,
          partySize: action.partySize,
          levelReq: plan.levelReq,
          forceType: plan.forceType,
          forceReq: plan.forceReq,
          partyMax: plan.partyMax,
          crystalValue: plan.crystalValue,
          crystalType: plan.crystalType,
          crystalVerified: plan.crystalVerified,
        });
      }
      await upsertClear.mutateAsync({
        accountId: action.accountId,
        weekKey: currentWeekKeyIso,
        characterId: action.characterId,
        bossSlug: action.bossSlug,
        bossName: plan.bossName,
        tier: plan.tier,
        sharedClearGroup: plan.sharedClearGroup,
        difficulty: plan.difficulty,
        crystalValue: plan.crystalValue,
        crystalType: plan.crystalType,
        periodKey,
        cleared: true,
        crystalSold: action.crystalSold,
      });
      pushToast(
        `Cleared ${titleCase(plan.difficulty)} ${plan.bossName} for ${resolvedCharacter?.name ?? 'that character'} — ${
          action.crystalSold ? 'crystal sold' : 'sale skipped'
        }`,
        'success',
      );
    } catch {
      // Already toasted + rolled back by the mutation's own onError.
    }
  };

  const execute = (suggestion: Suggestion) => {
    if (suggestion.action.kind === 'navigate') {
      const { to, search } = suggestion.action;
      navigate(search ? `${to}?${search}` : to, { viewTransition: true });
      close();
      return;
    }
    void executeClearBoss(suggestion.action);
    close();
  };

  // The `:` trigger: capture phase so it fires no matter what has focus, `preventDefault` so the
  // `:` never leaks into whatever's focused (or into the palette's own input once open), and a
  // generic "not a form field" check rather than special-casing react-select (its menu/input are
  // real DOM <input> elements, so the same check already excludes them).
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (isOpen) return;
      if (event.key !== ':' || event.ctrlKey || event.altKey || event.metaKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      setIsOpen(true);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen]);

  // Escape while open closes ONLY the palette — see the class doc comment above for why this must
  // be a capture-phase listener with stopPropagation rather than reusing Modal.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [suggestions.length, input]);

  if (!isOpen) {
    return null;
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (suggestions.length > 0) {
        setHighlightedIndex((i) => (i + 1) % suggestions.length);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (suggestions.length > 0) {
        setHighlightedIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = suggestions[highlightedIndex];
      if (chosen) {
        execute(chosen);
      }
    }
  };

  // Suggestions already come out of `buildSuggestions` grouped in the right order; this just
  // partitions the flat list into `[group, rows]` pairs for the group-heading rendering below.
  const groups = GROUP_ORDER.map((group) => ({
    group,
    rows: suggestions.filter((s) => s.group === group),
  })).filter((g) => g.rows.length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 pt-[15vh] backdrop-blur-sm"
      onPointerDown={(event) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pressStartedOnBackdrop.current) {
          close();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border-strong bg-bg-elevated shadow-2xl"
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleInputKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-listbox"
          aria-activedescendant={suggestions[highlightedIndex]?.id}
          aria-label="Command"
          placeholder="Jump to a page or character…"
          autoComplete="off"
          spellCheck={false}
          className="w-full border-b border-border bg-transparent px-4 py-3 text-base text-text placeholder:text-text-faint focus:outline-none"
        />
        <div id="command-palette-listbox" role="listbox" aria-label="Suggestions" className={selectClassNames.menuList()}>
          {suggestions.length === 0 ? (
            <p className={selectClassNames.noOptionsMessage()}>No matches</p>
          ) : (
            groups.map(({ group, rows }) => (
              <div key={group} role="group" aria-label={group} className={selectClassNames.group()}>
                <p className={selectClassNames.groupHeading()}>{group}</p>
                {rows.map((row) => {
                  const index = suggestions.indexOf(row);
                  const isFocused = index === highlightedIndex;
                  return (
                    <div
                      key={row.id}
                      id={row.id}
                      role="option"
                      aria-selected={isFocused}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => execute(row)}
                      className={selectClassNames.option({ isFocused, isSelected: false })}
                    >
                      {row.group === 'Characters' && (
                        <SuggestionAvatar
                          src={
                            row.avatarUrl != null && row.accountId != null && row.characterId != null
                              ? avatarProxyPath(row.accountId, row.characterId)
                              : null
                          }
                          label={row.label}
                        />
                      )}
                      {row.action.kind === 'clearBoss' && (
                        <BossIcon slug={row.action.bossSlug} name={row.action.bossName} />
                      )}
                      <span className="ml-2 flex min-w-0 flex-col items-start">
                        <span className="truncate">{row.label}</span>
                        {row.sublabel && <span className="truncate text-xs text-text-faint">{row.sublabel}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
